/**
 * Single product functionality.
 */

import Swiper, { Manipulation, Navigation, Pagination, Thumbs } from 'swiper';
import stickybits from 'stickybits';

/**
 * Summary as a sticky container on scroll.
 * Source: https://github.com/dollarshaveclub/stickybits
 */
stickybits( '.product .entry-summary', {
	useStickyClasses: true,
} );

/**
 * Trigger accordion.
 */
jQuery( '.product-block-content h2' ).on( 'click', function() {
	const parentEl = jQuery( this ).closest( '.product-block' );
	toggleProductBlock( parentEl );
} );

/**
 * Toggle bid block
 */
jQuery( '.bids-link' ).on( 'click', function() {
	const target = jQuery( this ).attr( 'href' );
	const blockEl = jQuery( target );
	toggleProductBlock( blockEl, true );
} );

/**
 * Toggle the description block
 */
jQuery( '.product-meta-link-info a' ).on( 'click', function() {
	const target = jQuery( this ).attr( 'href' );
	const blockEl = jQuery( target );
	toggleProductBlock( blockEl, true );
} );

/**
 * Toggles a product blocks accordion.
 *
 * @param {Object}  blockEl   the outer block element.
 * @param {boolean} forceOpen wether it should be forced to open.
 */
function toggleProductBlock( blockEl, forceOpen = false ) {
	const trigger = blockEl.find( 'h2' );
	const content = blockEl.find( '.product-block-inner-content' );

	if ( forceOpen ) {
		blockEl.removeClass( 'accordion-hidden' );
		trigger.attr( 'aria-expanded', 'true' );
		content.attr( 'aria-hidden', 'false' );
		return;
	}
	// Toggle class for the block which sets visibility.
	blockEl.toggleClass( 'accordion-hidden' );
	// Aria for the h2 trigger element.
	trigger.attr( 'aria-expanded', ( i, attr ) => {
		return 'true' === attr ? 'false' : 'true';
	} );
	// Aria for the hidden content.
	content.attr( 'aria-hidden', ( i, attr ) => {
		return 'true' === attr ? 'false' : 'true';
	} );
}

class SingleProductSlider {
	/**
	 * Have to continue using jQuery as the way variations work in WC
	 * means that you can only listen to the custom variation events
	 * with it.
	 */
	constructor() {
		const images = document.querySelectorAll( '.product-image-slider-item' );
		this.imageList = this._generateProductList( images );

		const thumbs = document.querySelectorAll( '.product-images-slider-item-thumb' );
		this.thumbList = this._generateProductList( thumbs );

		this.price = document.querySelector( '.summary-inner-wrapper > .price' );
		this.originalPrice = '';
		if ( this.price ) {
			this.originalPrice = this.price.cloneNode( true );
		}

		const thumbnailSliderSettings = {
			modules: [ Navigation, Manipulation ],
			direction: 'vertical',
			slidesPerView: 6,
			slidesPerGroup: 6,
			spaceBetween: 10,
			loopFillGroupWithBlank: true,
			allowTouchMove: true,
			threshold: 5,
			navigation: {
				nextEl: '.swiper-button-next',
				prevEl: '.swiper-button-prev',
			},
		};

		this.thumbnailSlider = new Swiper( '.product-images-slider-thumbnails', thumbnailSliderSettings );

		const productImageSlider = {
			modules: [ Navigation, Pagination, Thumbs ],
			preventClicks: false,
			preventClicksPropagation: false,
			pagination: {
				el: '.swiper-pagination',
				type: 'bullets',
				clickable: true,
			},
			navigation: {
				nextEl: '.swiper-button-next',
				prevEl: '.swiper-button-prev',
			},
			thumbs: {
				swiper: this.thumbnailSlider,
				slideThumbActiveClass: 'product-images-slider-item-thumb--selected',
			},
		};

		this.productImageSlider = new Swiper( '.product-image-slider', productImageSlider );
	}

	init() {
		this._addVariationListener();
		this._addResetListener();
		this._getVariationOnPageLoad();
	}

	/**
	 * Resets sliders to contain original images.
	 *
	 * @private
	 */
	_resetSliders = () => {
		const productImageElements = this.imageList.map( image => image.element );
		const thumbnailElements = this.thumbList.map( image => image.element ).reverse();

		this.productImageSlider.appendSlide( productImageElements );
		this.thumbnailSlider.prependSlide( thumbnailElements );
		this.thumbnailSlider.slideTo( 0, 0 );

		// this._createThumbnailBlankSlides();
		this._updatePrice();
	};

	/**
	 * Removes all but a given image from both sliders
	 *
	 * @param {Object} variation - variation object given from WC variation event
	 * @private
	 */
	_changeProductImage( variation ) {
		this.productImageSlider.removeAllSlides();
		this.thumbnailSlider.removeAllSlides();

		this.imageList.forEach( ( image, index ) => {
			const imageAttributes = JSON.stringify( image.attributes );
			const variationAttributes = JSON.stringify( variation.attributes );

			if ( imageAttributes === variationAttributes ) {
				const thumbnailElement = this.thumbList[ index ].element;

				this.productImageSlider.appendSlide( image.element );
				this.thumbnailSlider.appendSlide( thumbnailElement );
			}
		} );
	}

	/**
	 * Updates price from a variation
	 *
	 * @param {Object} [variation] - variation object given from WC variation event
	 * @private
	 */
	_updatePrice( variation ) {
		if ( ! variation ) {
			this.price.innerHTML = this.originalPrice.innerHTML;
			return;
		}

		this.price.innerHTML = variation.price_html;
	}

	/**
	 * Adds WC variation listener for data reset
	 *
	 * @private
	 */
	_addResetListener() {
		jQuery( '.variations_form' ).on( 'reset_data', this._resetSliders );
	}

	/**
	 * Adds WC variation listener for variation change
	 *
	 * @private
	 */
	_addVariationListener() {
		jQuery( '.variations_form' ).on( 'found_variation', ( event, variation ) => {
			this._changeProductImage( variation );
			this._updatePrice( variation );
		} );
	}

	/**
	 * Checks the current URL parameters for variations and attempts to set the image slider on page load using those.
	 *
	 * @private
	 */
	_getVariationOnPageLoad() {
		const currentURL = new URL( window.location.href );
		const currentURLParams = new URLSearchParams( currentURL.search )?.entries() ?? false;
		if ( ! currentURLParams ) {
			return;
		}
		const currentVariations = {
			attributes: {},
		};
		for ( const [ key, value ] of currentURLParams ) {
			if ( key.startsWith( 'attribute_' ) ) {
				currentVariations.attributes[ key ] = value;
			}
		}

		if ( Object.entries( currentVariations.attributes ).length !== 0 ) {
			this._changeProductImage( currentVariations );
		}
	}

	/*
	 * Generates a list of products containing
	 * elements and variation data that was provided
	 * from the DOM
	 *
	 * @typedef {Object} productListItem - item
	 * @property {Object}             attributes - things
	 * @property {HTMLElement}        element    - html element of product image
	 *
	 * @param    {Array[HTMLElement]} collection - Array of product elements
	 * @return {Array[productListItem]} - List of objects containing elements and variation data
	 *
	 * @private
	 */
	_generateProductList( collection ) {
		const collectionList = [];
		collection.forEach( image => {
			collectionList.push( {
				element: image,
				//attributes: JSON.parse( image.attributes ),
			} );
		} );
		return collectionList;
	}
}

// this was cauing a console error on other pages, so checked for single product page for now
const singleProductPageCheck = document.querySelector( '.single-product' );
if ( singleProductPageCheck ) {
	window.onload = () => {
		const images = document.querySelector( '.product-image-slider-item' );
		const thumbs = document.querySelector( '.product-images-slider-item-thumb' );

		// if there is at least one image, initialiser swiper sliders:
		if ( 0 !== images.childElementCount && 0 !== thumbs.childElementCount ) {
			const singleProductSlider = new SingleProductSlider();
			singleProductSlider.init();
		}
	};
}
